import * as T from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { renderSceneStill } from '@london-life/game/scene/still';
import { softwareRenderer } from '@london-life/game/scene/gpu';
import type { MapCommand } from '@london-life/game/city/types';
import { snapshotCanvas } from '@london-life/game/scene/snapshot';

// look down on the city from the reference angle
const VIEW_DIRECTION = new T.Vector3(0.14, 1.22, 1).normalize();
// the map opens close in over where the player is (as Lagos Life's does); zoom out for the whole city
const DISTANCE = { start: 175, min: 36, max: 640, focus: 150 };

// shared the map camera and controls
export function createMapViewport(
  element: HTMLDivElement,
  scene: T.Scene,
  onRender: (camera: T.Camera, width: number, height: number) => void,
  // where the map opens (the player's place; the middle of the city for a visitor)
  start: [number, number] = [-20, -4],
) {
  const camera = new T.PerspectiveCamera(30, 1, 1, 2000);
  const initialTarget = new T.Vector3(start[0], 0, start[1]);
  const place = (target: T.Vector3, distance: number) =>
    camera.position.copy(VIEW_DIRECTION).multiplyScalar(distance).add(target);
  place(initialTarget, DISTANCE.start);
  let renderer: T.WebGLRenderer | undefined;
  let canvas: HTMLCanvasElement;

  // keep a usable map when WebGL is unavailable
  try {
    renderer = new T.WebGLRenderer({ antialias: true, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    // keeps the map's own colours (the rooms use a warmer film look)
    renderer.toneMapping = T.NeutralToneMapping;
    renderer.shadowMap.enabled = !softwareRenderer(renderer);
    renderer.shadowMap.type = T.PCFSoftShadowMap;
    // the map is drawn only when it moves: the shadows are worked out once, not every frame
    renderer.shadowMap.autoUpdate = false;
    renderer.shadowMap.needsUpdate = true;
    canvas = renderer.domElement;
  } catch {
    canvas = document.createElement('canvas');
  }
  canvas.setAttribute('aria-hidden', 'true');
  element.appendChild(canvas);
  const controls = new OrbitControls(camera, canvas);
  controls.target.copy(initialTarget);
  controls.enableRotate = false;
  controls.enableDamping = false;
  controls.screenSpacePanning = false;
  controls.minDistance = DISTANCE.min;
  controls.maxDistance = DISTANCE.max;
  controls.zoomToCursor = true;
  controls.mouseButtons = { LEFT: T.MOUSE.PAN, MIDDLE: T.MOUSE.DOLLY, RIGHT: T.MOUSE.PAN };
  controls.touches = { ONE: T.TOUCH.PAN, TWO: T.TOUCH.DOLLY_PAN };
  controls.update();
  let frame = 0,
    focusFrame = 0,
    disposed = false;

  // render only when the view changes
  const render = () => {
    frame = 0;
    if (disposed || document.hidden) return;
    const width = element.clientWidth,
      height = element.clientHeight;
    scene.updateMatrixWorld(true);
    camera.updateMatrixWorld(true);
    if (renderer) renderer.render(scene, camera);
    else renderSceneStill(scene, camera, canvas, width, height);
    onRender(camera, width, height);
  };
  const requestRender = () => {
    if (!frame && !disposed) frame = requestAnimationFrame(render);
  };
  controls.addEventListener('change', requestRender);
  // once someone moves the map themselves, it is not moved for them on load
  let touched = false;
  controls.addEventListener('start', () => (touched = true));
  const resize = () => {
    const width = element.clientWidth,
      height = element.clientHeight;
    if (!width || !height) return;
    camera.aspect = width / height;
    camera.fov = width < 640 ? 42 : 30;
    camera.updateProjectionMatrix();
    renderer?.setSize(width, height);
    requestRender();
  };
  const observer = new ResizeObserver(resize);
  observer.observe(element);
  resize();
  document.addEventListener('visibilitychange', requestRender);

  // move the camera along its view line for every zoom change
  const distance = () => camera.position.distanceTo(controls.target);
  const command = (action: MapCommand) => {
    cancelAnimationFrame(focusFrame);
    if (action === 'reset' || action === 'north') {
      controls.target.copy(initialTarget);
      place(initialTarget, DISTANCE.start);
    } else
      place(
        controls.target,
        T.MathUtils.clamp(distance() * (action === 'in' ? 1 / 1.3 : 1.3), DISTANCE.min, DISTANCE.max),
      );
    controls.update();
    requestRender();
  };
  // glide towards a chosen place
  const focus = (x: number, z: number, onDone?: () => void) => {
    cancelAnimationFrame(focusFrame);
    const startTarget = controls.target.clone(),
      startDistance = distance();
    const target = new T.Vector3(x, 0, z),
      started = performance.now();
    const step = () => {
      const progress = Math.min(1, (performance.now() - started) / 900),
        eased = progress * progress * (3 - 2 * progress);
      controls.target.lerpVectors(startTarget, target, eased);
      place(controls.target, T.MathUtils.lerp(startDistance, DISTANCE.focus, eased));
      controls.update();
      requestRender();
      if (progress < 1) focusFrame = requestAnimationFrame(step);
      else onDone?.();
    };
    focusFrame = requestAnimationFrame(step);
  };
  // keep something moving in view (a journey): the camera rides along at `distance`, easing towards it
  const follow = (x: number, z: number, wanted: number) => {
    cancelAnimationFrame(focusFrame);
    const target = new T.Vector3(x, 0, z);
    controls.target.lerp(target, 0.12);
    place(controls.target, T.MathUtils.lerp(distance(), wanted, 0.06));
    controls.update();
    requestRender();
  };
  // release the map when leaving the homepage
  const dispose = () => {
    disposed = true;
    cancelAnimationFrame(frame);
    cancelAnimationFrame(focusFrame);
    observer.disconnect();
    controls.dispose();
    document.removeEventListener('visibilitychange', requestRender);
    renderer?.dispose();
    renderer?.forceContextLoss();
    canvas.remove();
  };
  return {
    camera,
    command,
    focus,
    follow,
    // where the map opens (the player's place, once known): moves there unless the map was already moved
    setStart: (x: number, z: number) => {
      initialTarget.set(x, 0, z);
      if (touched) return;
      controls.target.copy(initialTarget);
      place(initialTarget, DISTANCE.start);
      controls.update();
      requestRender();
    },
    // draw the next frame (something moved; the shadows stay as they are)
    draw: requestRender,
    // draw again (and work the shadows out again: something on the map changed)
    refresh: () => {
      if (renderer) renderer.shadowMap.needsUpdate = true;
      requestRender();
    },
    // the player's data saver: fewer pixels for older phones and mobile data
    setDataSaver: (on: boolean) => {
      if (!renderer) return;
      renderer.setPixelRatio(on ? 1 : Math.min(devicePixelRatio, 1.5));
      renderer.shadowMap.enabled = !on && !softwareRenderer(renderer);
      renderer.shadowMap.needsUpdate = true;
      scene.traverse((object) => {
        if (object instanceof T.Mesh) (object.material as T.Material).needsUpdate = true;
      });
      resize();
    },
    dispose,
    // a photo of the map as it is now (the in-game camera)
    snapshot: () =>
      snapshotCanvas(canvas, () => {
        if (renderer) renderer.render(scene, camera);
        else renderSceneStill(scene, camera, canvas, element.clientWidth, element.clientHeight);
      }),
    mode: renderer ? ('3d' as const) : ('still' as const),
    // drawing without a graphics chip: no shadows, and the map keeps its blocks
    slow: renderer ? softwareRenderer(renderer) : true,
  };
}
